import { createSlice, createAction, createListenerMiddleware, TypedStartListening, createAsyncThunk } from "@reduxjs/toolkit";
import { ApplicationState, AppDispatch, resetApp } from "./store";
import { generateInitialState } from "utils/common";
import { getUser } from "api/api";

interface Entity {
    companyId?: string;
}

export const MAX_RETRY_COUNT = 8;
const sliceName = "authorisation";

// Middlewares
export const authorisationListener = createListenerMiddleware();
export const startAuthorisationListener = authorisationListener.startListening as TypedStartListening<ApplicationState, AppDispatch>;


// Thunks
export const authorise = createAsyncThunk<string, void, { state: ApplicationState }>(
    `${sliceName}/AUTHORISE`,
    async (_: any, thunkApi) => {
        try {
            const response = await getUser();

            return thunkApi.fulfillWithValue(response.AdminProfile.CompanyId)
        } catch (error) {
            return thunkApi.rejectWithValue((error as any)?.response?.statusText);
        }
    }
);

// Actions
export const initAuthorise = createAction(`${sliceName}/INIT`);
export const authoriseSuccess = createAction(`${sliceName}/AUTHORISE_SUCCESS`, (payload: BokaMera.User) => {
    return {
        payload
    }
});
export const countAuthoriseFailures = createAction(`${sliceName}/COUNT_FAILED`, (err: any) => {
    return {
        payload: err
    }
});
export const setAuthoriseSuccess = createAction(`${sliceName}/SET_AUTHORISE_SUCCESS`);


// Slice
export const initialState = {
    ...generateInitialState<Entity>(),
    retryCount: 0,
    loginProgramtically: false,
};

const authorisationSlice = createSlice({
    name: sliceName,
    initialState,
    reducers: {},
    extraReducers(builder) {

        builder.addMatcher(authorise.fulfilled.match, (state, { payload }) => {
            state.isSuccess = true;
            state.retryCount = 0;
            state.isLoading = false;
            state.isError = false;
            state.error = undefined;

            if (!state.entity) {
                state.entity = {}
            }
            state.entity.companyId = payload;
        });

        builder.addMatcher(authorise.pending.match, (state) => {
            state.isLoading = true;
        });

        builder.addMatcher(authorise.pending.match, (state, { payload }) => {
            state.isLoading = false;
            state.retryCount = 0;
            state.isError = true;
            state.error = payload;
        });

        builder.addMatcher(authoriseSuccess.match, (state, { payload }) => {
            state.isSuccess = true;
            state.retryCount = 0;
            state.isLoading = false;
            state.isError = false;
            state.error = undefined;

            if (!state.entity) {
                state.entity = {}
            }
            state.entity.companyId = payload.AdminProfile.CompanyId;
        });

        builder.addMatcher(countAuthoriseFailures.match, (state, { payload }) => {
            state.isLoading = false;
            state.retryCount++;
            state.isError = true;
            state.error = payload;
            state.isSuccess = false;
        });


        builder.addMatcher(initAuthorise.match, (state) => {
            state.isInitialized = true;
            if (!state.entity) {
                state.entity = {}
            }
        });

        builder.addMatcher(setAuthoriseSuccess.match, (state) => {
            state.isSuccess = true;
            state.isError = false;
            state.retryCount = 0;
            state.error = undefined;
        });

        builder.addMatcher(resetApp.match, () => {
            return initialState;
        });
    },
});

// selectors
export const isAuthorised = (s: ApplicationState) => Boolean(s.license.isInitialized && s.license.entity?.license && s.authorisation.entity?.companyId);
export const isAuthorisationDone = (s: ApplicationState) => Boolean(s.license.isInitialized && s.authorisation.isInitialized);

export default authorisationSlice;
